import { COACH_GREEN } from '@/lib/coaching-mode'

export type Accent = 'lime' | 'ocean' | 'rose' | 'aurora' | 'ember' | 'gold' | 'mono'

const STORAGE_KEY = 'goaltree_accent'
const ACCENT_CLASSES = [
  'accent-ocean',
  'accent-rose',
  'accent-aurora',
  'accent-ember',
  'accent-gold',
  'accent-mono',
]

export const ACCENT_COLORS: Record<Accent, string> = {
  lime: '#a8ff3e',
  ocean: '#3DA9FC',
  rose: '#FF5C8A',
  aurora: '#A66BFF',
  ember: '#FF6452',
  gold: '#FFB020',
  mono: '#E5E5E5',
}

// Picker order. Lime and Monochrome are free; the rest unlock with Pro.
export const ACCENT_OPTIONS: { id: Accent; color: string; pro: boolean; category: string }[] = [
  { id: 'lime', color: '#a8ff3e', pro: false, category: 'mind' },
  { id: 'ocean', color: '#3DA9FC', pro: true, category: 'career' },
  { id: 'rose', color: '#FF5C8A', pro: true, category: 'relationships' },
  { id: 'aurora', color: '#A66BFF', pro: true, category: 'creative' },
  { id: 'ember', color: '#FF6452', pro: true, category: 'health' },
  { id: 'gold', color: '#FFB020', pro: true, category: 'finance' },
  { id: 'mono', color: 'var(--foreground)', pro: false, category: '' },
]

// Indexed by weekday (0 = Sunday); settings.accentWhy.* explains each pick.
export const ALL_ACCENTS: Accent[] = ['mono', 'ocean', 'ember', 'aurora', 'gold', 'rose', 'lime']

// Daily accent rotation (Pro): the accent cycles through the palette by weekday.
const ROTATE_KEY = 'goaltree_accent_rotate'

export function dayAccent(day = new Date().getDay()): Accent {
  return ALL_ACCENTS[day % ALL_ACCENTS.length]
}
function readRotate(): boolean {
  try {
    return localStorage.getItem(ROTATE_KEY) === '1'
  } catch {
    return false
  }
}

const FLAME =
  'M40 16C44 30 54 36 56 50C60 42 59 34 57 28C66 36 70 46 70 58C70 72 60 82 48 82C36 82 26 72 26 59C26 44 38 36 40 16ZM48 54C52 60 56.5 63 56.5 69C56.5 74 52.5 78 48 78C43.5 78 39.5 74 39.5 69C39.5 63 44 60 48 54Z'

// The app icon tinted to the user's accent.
function faviconSvg(color: string) {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96"><rect width="96" height="96" rx="22" fill="${color}"/><path d="${FLAME}" transform="translate(0 -1)" fill="#1a2600" fill-rule="evenodd"/></svg>`
}

let appliedAccent: Accent = 'lime'

function updateFavicon(accent: Accent) {
  const coaching = document.documentElement.classList.contains('coaching')
  const svg = faviconSvg(coaching ? COACH_GREEN : ACCENT_COLORS[accent])
  const url = `data:image/svg+xml,${encodeURIComponent(svg)}`
  // Retarget in place: removing nodes React hoisted crashes its later unmount (removeChild on null).
  const links = document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]')
  if (!links.length) {
    const link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
    return updateFavicon(accent)
  }
  links.forEach((link) => {
    link.removeAttribute('sizes')
    link.type = 'image/svg+xml'
    link.href = url
  })
}

// Coaching mode tints the icon coach green; re-run when it toggles.
export function refreshFavicon() {
  updateFavicon(appliedAccent)
}

function applyAccent(accent: Accent) {
  appliedAccent = accent
  const html = document.documentElement
  ACCENT_CLASSES.forEach((c) => html.classList.remove(c))
  if (accent !== 'lime') html.classList.add(`accent-${accent}`)
  updateFavicon(accent)
}

// Apply the accent before first React paint to avoid flash — today's rotated
// accent when rotation is on, otherwise the saved one.
if (typeof document !== 'undefined') {
  if (readRotate()) {
    applyAccent(dayAccent())
  } else {
    const saved = localStorage.getItem(STORAGE_KEY) as Accent | null
    if (saved) applyAccent(saved)
  }
}

import { useCallback, useEffect, useSyncExternalStore } from 'react'

function readStored(): Accent {
  try {
    return (localStorage.getItem(STORAGE_KEY) as Accent) ?? 'lime'
  } catch {
    return 'lime'
  }
}

// Every useAccentTheme() call shares one source of truth (localStorage). A
// same-tab write emits to all subscribers so sibling instances — notably the
// always-mounted root layout — re-read too; otherwise a stale rotate=true there
// would re-apply the rotated accent on the next re-render or tab focus, undoing
// a manual pick.
const listeners = new Set<() => void>()
function emitAccentChange() {
  for (const l of listeners) l()
}
function subscribeAccent(cb: () => void) {
  listeners.add(cb)
  if (typeof window !== 'undefined') window.addEventListener('storage', cb)
  return () => {
    listeners.delete(cb)
    if (typeof window !== 'undefined') window.removeEventListener('storage', cb)
  }
}

export function useAccentTheme() {
  const stored = useSyncExternalStore(subscribeAccent, readStored, () => 'lime' as Accent)
  const rotate = useSyncExternalStore(subscribeAccent, readRotate, () => false)
  // When rotation is on, today's weekday accent overrides the saved pick.
  const accent = rotate ? dayAccent() : stored

  useEffect(() => {
    applyAccent(accent)
  }, [accent])

  // While rotating, refresh when the tab is shown again so it tracks the day.
  useEffect(() => {
    if (!rotate) return
    const onVisible = () => {
      if (document.visibilityState === 'visible') applyAccent(dayAccent())
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [rotate])

  const setAccent = useCallback((next: Accent) => {
    // Manually choosing an accent turns rotation off.
    try {
      localStorage.setItem(ROTATE_KEY, '0')
      localStorage.setItem(STORAGE_KEY, next)
    } catch {}
    applyAccent(next)
    emitAccentChange()
  }, [])

  const setRotate = useCallback((on: boolean) => {
    try {
      localStorage.setItem(ROTATE_KEY, on ? '1' : '0')
    } catch {}
    applyAccent(on ? dayAccent() : readStored())
    emitAccentChange()
  }, [])

  return { accent, rotate, setAccent, setRotate }
}
